import { VariantPicker as RecoveredVariantPicker } from 'vl-recovered/product-variant-picker'
import { replaceNodeChildren } from 'src/scripts/utils/dom'
import { $el, $list, $elParent, whenDefined } from 'src/scripts/utils/utils'
import {
  buildVariantLabelAria,
  getOptionState,
  isVariantActivationKey,
  selectVariantWithFallback,
} from './product-variant-picker-state'

const OPTION_INPUTS_SELECTOR = '[data-variant-picker-option-inputs]'
const OPTION_LABELS_SELECTOR = '[data-variant-picker-option-labels]'
const DROPDOWN_LABEL_SELECTOR = '[data-dropdown-variant-picker-label]'

export class VariantPicker extends RecoveredVariantPicker {
  updateLabels(): void {
    const inputContainers = $list(OPTION_INPUTS_SELECTOR, this)
    const labelContainers = $list(OPTION_LABELS_SELECTOR, this)

    inputContainers.forEach((inputContainer, idx) => {
      const labels = $list('label', labelContainers[idx])
      const inputs = $list('input', inputContainer)
      const input = this.getCheckedInput(inputs)
      const dropdownLabel = $el(
        DROPDOWN_LABEL_SELECTOR,
        labelContainers[idx],
      )

      if (dropdownLabel) {
        dropdownLabel.toggleAttribute('no-available', !this.variant?.available)
      }

      labels.forEach(label => {
        const isSelected = label.getAttribute('for') === input.id

        if (dropdownLabel && isSelected) {
          const dropdown = $elParent('dropdown-opener', dropdownLabel)
          replaceNodeChildren(dropdownLabel, label)
          dropdown.trigger()
        }

        label.classList.toggle('selected', isSelected)
        label.setAttribute('aria-checked', isSelected ? 'true' : 'false')

        const optionName = labelContainers[idx]?.getAttribute(
          'option-picker-name',
        )
        const labelText =
          label.getAttribute('data-value') || label.textContent?.trim()
        const ariaLabel = buildVariantLabelAria(
          optionName,
          labelText,
          isSelected,
        )

        if (ariaLabel) label.setAttribute('aria-label', ariaLabel)
      })
    })
  }

  setVariant(): void {
    const options = $list(OPTION_INPUTS_SELECTOR, this)
    const selectedOptions = options.map(
      option => this.getCheckedInput($list('input', option)).value,
    )
    const result = selectVariantWithFallback(this.variants, selectedOptions)

    this.variant = result.variant

    if (result.usedFallback) {
      options.forEach((option, index) => {
        const input = $el(
          `input[value="${result.selectedOptions[index]}"]`,
          option,
        )
        if (input) input.checked = true
      })
    }

    if (!this.variant) {
      whenDefined('product-form').then(() => this.setUnavailable())
    }
  }

  handleLabelKeydown = (event: KeyboardEvent): void => {
    if (!isVariantActivationKey(event)) return

    event.preventDefault()
    const target = event.target as HTMLElement
    const input = $el(`input[id="${target.getAttribute('for')}"]`, this)

    if (input) {
      input.dispatchEvent(new MouseEvent('click', { button: 0, which: 1 }))
    }
  }

  getAllVariantsByOption(prevOptions) {
    const variants = this.variants.map(variant => variant.options)

    if (!prevOptions) return variants

    return variants.filter(options =>
      prevOptions.every(prevOption => options.includes(prevOption.value)),
    )
  }

  async setOptions(options, possibleVariants, allVariants) {
    for (const option of options) {
      const state = getOptionState(
        option.value,
        possibleVariants,
        allVariants,
      )
      const labelSelector = `label[for="${option.id}"]`
      const label =
        $el(labelSelector, this) ||
        (await this.getDropdownElementBySelector(labelSelector))

      if (!label) continue

      label.toggleAttribute('no-available', state.disabled)
      label.toggleAttribute('out-of-stock', state.outOfStock)
      label.toggleAttribute('no-exist', state.noExist)
      label.setAttribute('aria-disabled', state.disabled ? 'true' : 'false')
    }
  }

  setOptionsAvailable(): void {
    const options = $list(OPTION_INPUTS_SELECTOR, this)
    const firstOptions = options[0] && $list('input', options[0])

    if (!firstOptions?.length) return

    const firstOption = this.getCheckedInput(firstOptions)
    this.setOptions(
      firstOptions,
      this.getVariantsByOption([]),
      this.getAllVariantsByOption([]),
    )

    const secondOptions = options[1] && $list('input', options[1])
    if (!secondOptions) return

    const secondOption = this.getCheckedInput(secondOptions)
    this.setOptions(
      secondOptions,
      this.getVariantsByOption([firstOption]),
      this.getAllVariantsByOption([firstOption]),
    )

    const thirdOptions = options[2] && $list('input', options[2])
    if (!thirdOptions) return

    this.setOptions(
      thirdOptions,
      this.getVariantsByOption([firstOption, secondOption]),
      this.getAllVariantsByOption([firstOption, secondOption]),
    )
  }
}
